// types/remisiones.types.ts

export interface SysIRemisionesTemporales {
  Rmt_Id?: number;
  Rmt_Es_Id?: number;
  Rmt_Lr_Id?: number;
  Rmt_FechaInicio?: Date;
  Rmt_FechaFin?: Date;
  Rmt_Observaciones?: string | null;
  Rmt_Estado?: boolean;
}

export interface SysALaboratoriosRemisiones {
  Lr_Id: number;
  Lr_Nombre: string;
  Lr_Nit: string;
  Lr_Encargado: string;
  Lr_Telefono?: string | null;
  Lr_Correo?: string | null;
  Lr_Direccion?: string | null;
  Lr_Estado: boolean;
  Lr_Color?: string | null;
}

export interface SysIUsuarios {
  Us_Id: number;
  Us_Nombre: string;
  Us_Email?: string;
  Us_Estado: boolean;
}

export interface SysIEstudios {
  Es_Id: number;
  Es_Nombre: string;
  Es_Codigo: string;
  Es_Estado: boolean;
  Es_Aa_Id?: number;
  Es_Lr_Id?: number;
  SysA_AreasAnalisis?: {
    Aa_Nombre: string;
  };
}

export interface SysIOrdenes {
  Ord_Id: number;
  Ord_Numero: string;
  Ord_FechaCreacion: Date;
  Ord_Estado: boolean;
  Paciente?: SysIPacientes;
}

export interface SysIPacientes {
  Pc_Id: number;
  Pc_Nombre: string;
  Pc_Apellido1: string;
  Pc_Apellido2?: string;
  Pc_Identificacion: string;
}

export interface SysIRemisionesDetalle {
  Rmd_Id?: number;
  Rmd_Rm_Id: number;
  Rmd_Ord_Id: number;
  Rmd_Es_Id: number;
  Rmd_Fecha: Date;
  Rmd_Nota?: string | null;
  Rmd_Estado?: boolean | null;
  Rmd_Cantidad: number;
  Rmd_Urgente: boolean;
  Rmd_ResultadoRecibido: boolean;
  Rmd_FechaResultado?: Date | null;
  
  // Navegación
  Orden?: SysIOrdenes;
  Estudio?: SysIEstudios;
}

export interface SysIRemisiones {
  Rm_Id?: number;
  Rm_Lr_Id: number;
  Rm_FechaCreacion: Date;
  Rm_FechaModificacion?: Date | null;
  Rm_Estado: number; // ETypeRemission
  Rm_TemperaturaNevera?: string | null;
  Rm_TemperaturaAmbiente?: string | null;
  Rm_Notas?: string | null;
  Rm_FechaEnvio?: Date | null;
  Rm_ResponsableEnvio?: string | null;
  Rm_Us_Id: number;
  
  // Navegación
  LaboratorioRemision?: SysALaboratoriosRemisiones;
  Usuario?: SysIUsuarios;
  DetalleRemisiones?: SysIRemisionesDetalle[];
}

export enum ETypeRemission {
  CREADA = 1,
  IMPRESA = 2,
  ENVIADA = 3,
  EN_PROCESO = 4,
  COMPLETADA = 5,
  CANCELADA = 6
}

/** Remisión en la que salió un estudio de una orden (Remission/GetStudyRemissionInfo). */
export interface RemisionEstudioInfo {
  Rm_Id: number;
  Rm_Estado: ETypeRemission;
  Laboratorio: string;
  CreadaPor: string;
  Rm_FechaCreacion: string;
  Rm_FechaEnvio: string | null;
  Rm_ResponsableEnvio: string | null;
  Rmd_Urgente: boolean;
  Rmd_ResultadoRecibido: boolean;
  Rmd_FechaResultado: string | null;
}

export interface EstadoRemisionLookup {
  id: ETypeRemission;
  nombre: string;
}

// Tipos para filtros
export interface FiltrosRemisiones {
  fechaInicio: Date;
  fechaFin: Date;
  estado: ETypeRemission | number | null;
  laboratorioId: number | null;
  usuarioId: number | null;
}

// Tipos para requests del API
export interface ChangeEstadoRequest {
  Id: number;
  NuevoEstado: ETypeRemission;
}

export interface DeleteRequest {
  Id: number;
}

export interface UpdateResultadoRequest {
  DetalleId: number;
  ResultadoRecibido: boolean;
}

// Tipos para las respuestas del API
export interface ApiResponse<T> {
  internalCode: number;
  message: string;
  data: T;
}

// Tipos para toolbar items (corregir el error de TypeScript)
export interface ToolbarButtonItem {
  location: 'before' | 'after' | 'center';
  widget: 'dxButton';
  options: {
    icon?: string;
    text?: string;
    type?: 'default' | 'success' | 'normal' | 'danger';
    hint?: string;
    onClick: () => void | Promise<void>;
  };
}

export interface LaboratorioRemision {
  Lr_Id: number;
  Lr_Nombre: string;
  Lr_Direccion?: string;
  Lr_Telefono?: string;
  Lr_Email?: string;
  Lr_Estado: boolean;
}

// Nuevos tipos para órdenes pendientes de remisión
export interface OrdenEstudioRemision {
  Ord_Id: number;
  Ord_Numero: string;
  Ord_FechaCreacion: Date;
  Paciente_Nombre: string;
  Paciente_Identificacion: string;
  Es_Id: number;
  Es_Codigo: string;
  Es_Nombre: string;
  Area_Nombre: string;
  Roe_Id: number;
  Tiene_Resultado: boolean;
  Fecha_Resultado?: Date | null;
}

export interface EstudioDisponibleRemision {
  Es_Id: number;
  Es_Codigo: string;
  Es_Nombre: string;
  Area_Nombre: string;
  Lr_Id: number;
  Laboratorio_Nombre: string;
}

// Tipos actualizados para el modal
export interface EstudioDisponible extends SysIEstudios {
  // Ya incluido en SysIEstudios
}

export interface EstudioSeleccionado extends OrdenEstudioRemision {
  Rmd_Cantidad: number;
  Rmd_Urgente: boolean;
  Rmd_Nota?: string;
  _isExisting?: boolean; 
  _originalRmdId?: number | null; // Indica si es un estudio existente o nuevo
}

export interface RemisionFormData {
  laboratorioId: number | null;
  fechaInicio: Date | null;      
  fechaFin: Date | null;         
  temperaturaNevera: string;
  temperaturaAmbiente: string;
  notas: string;
  responsableEnvio: string;
}

export interface RemisionData {
  Rm_Id: number;
  Rm_Lr_Id: number;
  Rm_FechaCreacion: string;
  Rm_FechaModificacion?: string;
  Rm_Estado: number;
  Rm_TemperaturaNevera?: string;
  Rm_TemperaturaAmbiente?: string;
  Rm_Notas?: string;
  Rm_FechaEnvio?: string;
  Rm_ResponsableEnvio?: string;
  Rm_Us_Id: number;
  LaboratorioRemision?: {
    Lr_Id: number;
    Lr_Nombre: string;
    Lr_Telefono?: string;
    Lr_Correo?: string;
  };
  Usuario?: {
    Us_Id: number;
    Us_Nombre: string;
  };
  DetalleRemisiones: any[];
}

export type ToolbarItem = ToolbarButtonItem | 'searchPanel';